import React, { useContext, useState, useEffect } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PhotoCamera, DeleteForever, Download } from '@mui/icons-material';
import AuthContext from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { profile, logoutUser, authTokens, backendUrl, refreshProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: profile?.username || '',
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    sex: profile?.sex || '',
    date_of_birth: profile?.date_of_birth ? dayjs(profile.date_of_birth) : null
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  // Sync form data with profile when profile updates
  useEffect(() => {
    if (profile) {
      setFormData({
        email: profile.email || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        sex: profile.sex || '',
        date_of_birth: profile.date_of_birth ? dayjs(profile.date_of_birth) : null
      });
    }
  }, [profile]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      date_of_birth: date
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!profilePictureFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('profile_picture', profilePictureFile);

      const response = await fetch(`${backendUrl}api/upload-profile-picture/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authTokens?.access}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload profile picture');
      }

      // Refresh the profile data
      await refreshProfile();

      // Update timestamp to bust cache
      setImageTimestamp(Date.now());

      // Clear the preview and file
      setProfilePictureFile(null);
      setProfilePicturePreview(null);

      setSnackbar({
        open: true,
        message: 'Profile picture updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    if (passwordData.new_password !== passwordData.confirm_new_password) {
      newErrors.confirm_new_password = "Passwords don't match";
    }
    if (passwordData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/auth/profile/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens?.access}`
        },
        body: JSON.stringify({
          ...formData,
          date_of_birth: formData.date_of_birth ? formData.date_of_birth.format('YYYY-MM-DD') : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      // Refresh the profile data in AuthContext
      await refreshProfile();

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/auth/update-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens?.access}`
        },
        body: JSON.stringify({
          old_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }

      setSnackbar({
        open: true,
        message: 'Password changed successfully! Please login again.',
        severity: 'success'
      });
      setPasswordDialogOpen(false);
      // Logout user after password change
      setTimeout(() => logoutUser(), 4000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setFormData({
      email: profile?.email || '',
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      sex: profile?.sex || '',
      date_of_birth: profile?.date_of_birth ? dayjs(profile.date_of_birth) : null
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleProceedToConfirmDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmDialogOpen(true);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setDeleteConfirmDialogOpen(false);
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);

    try {
      const response = await fetch(`${backendUrl}api/delete-account/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens?.access}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }

      // Store email for feedback page before navigating
      const userEmail = profile?.email || formData.email;

      // Account deleted successfully, navigate to feedback page
      navigate('/account-deleted', { state: { email: userEmail } });
    } catch (error) {
      setDeletingAccount(false);
      setDeleteConfirmDialogOpen(false);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete account. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await fetch(`${backendUrl}api/export-csv/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authTokens?.access}` }
      });
      if (!response.ok) throw new Error('Failed to export CSV');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `couplendar_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'CSV exported successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to export CSV. Please try again.', severity: 'error' });
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await fetch(`${backendUrl}api/export-user-data/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokens?.access}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export data');
      }

      const data = await response.json();

      // Create a blob from the JSON data
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `couplendar_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Your data has been exported successfully!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to export data. Please try again.',
        severity: 'error'
      });
    } finally {
      setExportingData(false);
    }
  };

  return (
    <>
    <SEO title="Profile - Couplendar" />
    <Box sx={{
      minHeight: '100vh',
      background: '#F9F7F4',
      py: { xs: 3, md: 4 }
    }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: '#333',
            mb: 3
          }}
        >
          Profile Settings
        </Typography>

      {/* Profile Picture Section */}
      <Card sx={{
        mb: 3,
        background: 'white',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        borderRadius: 3,
        border: 'none'
      }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3
          }}>
            <Avatar
              src={profilePicturePreview || (profile?.profile_picture ? `${backendUrl}${profile.profile_picture}?t=${imageTimestamp}` : null)}
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                backgroundColor: '#7B6B8D'
              }}
            >
              {profile?.first_name?.charAt(0)}{profile?.last_name?.charAt(0)}
            </Avatar>
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleProfilePictureChange}
              />
              <label htmlFor="profile-picture-upload">
                <IconButton color="primary" aria-label="upload picture" component="span">
                  <PhotoCamera />
                </IconButton>
              </label>
              {profilePictureFile && (
                <Button
                  variant="contained"
                  onClick={handleUploadProfilePicture}
                  disabled={loading}
                  sx={{ ml: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Upload'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box component="form" sx={{ mt: 3 }}>
        <TextField
          fullWidth
          margin="normal"
          label="Email *"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleProfileChange}
          required
          inputProps={{
            readOnly: true,
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="First Name (optional)"
          name="first_name"
          value={formData.first_name}
          onChange={handleProfileChange}
          inputProps={{ maxLength: 50 }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Last Name (optional)"
          name="last_name"
          value={formData.last_name}
          onChange={handleProfileChange}
          inputProps={{ maxLength: 50 }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Sex (optional)</InputLabel>
          <Select
            name="sex"
            value={formData.sex}
            label="Sex (optional)"
            onChange={handleProfileChange}
          >
            <MenuItem value="">Not specified</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date of Birth (optional)"
            value={formData.date_of_birth}
            onChange={handleDateChange}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal"
              }
            }}
          />
        </LocalizationProvider>

        <Box sx={{
          mt: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.2,
              background: '#7B6B8D',
              boxShadow: 'none',
              '&:hover': {
                background: '#6A5A7A',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetForm}
            disabled={loading}
            sx={{
              borderRadius: 2,
              py: 1.2,
              borderColor: '#7B6B8D',
              color: '#7B6B8D',
              '&:hover': {
                borderColor: '#6A5A7A',
                backgroundColor: 'rgba(184, 167, 212, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => setPasswordDialogOpen(true)}
            sx={{
              borderRadius: 2,
              py: 1.2,
              borderColor: '#C4856A',
              color: '#C4856A',
              '&:hover': {
                borderColor: '#B0745A',
                backgroundColor: 'rgba(244, 181, 196, 0.04)'
              }
            }}
          >
            Change Password
          </Button>
        </Box>

        {/* Export Data Section */}
        <Card sx={{
          mt: 4,
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          borderRadius: 3,
          border: '1px solid #e3f2fd'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                mb: 2
              }}
            >
              Data Export
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Download your activity data as CSV (for spreadsheets) or JSON (full backup including all settings).
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={exportingCsv ? <CircularProgress size={20} /> : <Download />}
                onClick={handleExportCsv}
                disabled={exportingCsv}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': { borderColor: '#115293', backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                }}
              >
                {exportingCsv ? 'Exporting...' : 'Export as CSV'}
              </Button>
              <Button
                variant="outlined"
                startIcon={exportingData ? <CircularProgress size={20} /> : <Download />}
                onClick={handleExportData}
                disabled={exportingData}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': { borderColor: '#115293', backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                }}
              >
                {exportingData ? 'Exporting...' : 'Export as JSON'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Delete Account Section */}
        <Card sx={{
          mt: 4,
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          borderRadius: 3,
          border: '1px solid #ffebee'
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: '#d32f2f',
                mb: 2
              }}
            >
              Danger Zone
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 2
              }}
            >
              Once you delete your account, there is no going back. All your data, including relationships, chores, and time-off records will be permanently deleted.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DeleteForever />}
              onClick={handleOpenDeleteDialog}
              sx={{
                borderRadius: 2,
                py: 1.2,
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              Delete My Account
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Change Password Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Current Password"
              name="current_password"
              type="password"
              value={passwordData.current_password}
              onChange={handlePasswordChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="New Password"
              name="new_password"
              type="password"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              error={!!errors.new_password}
              helperText={errors.new_password}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Confirm New Password"
              name="confirm_new_password"
              type="password"
              value={passwordData.confirm_new_password}
              onChange={handlePasswordChange}
              error={!!errors.confirm_new_password}
              helperText={errors.confirm_new_password}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleChangePassword}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Initial Delete Account Warning Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600 }}>
          Delete Account?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you absolutely sure you want to delete your account? This will permanently:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Delete all your relationships and associated data</li>
            <li>Remove all chore and time-off records</li>
            <li>Delete your profile and personal information</li>
            <li>Erase all statistics and history</li>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
            This action is permanent and irreversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleProceedToConfirmDelete}
            color="error"
            variant="contained"
          >
            I Understand, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Confirmation Dialog with Progress */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={deletingAccount ? null : handleCloseConfirmDeleteDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={deletingAccount}
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600 }}>
          {deletingAccount ? 'Deleting Your Account...' : 'Final Confirmation'}
        </DialogTitle>
        <DialogContent>
          {deletingAccount ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={60} sx={{ color: '#d32f2f', mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                Deleting your account
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Please wait while we permanently remove all your data...
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please do not close this window or navigate away until the process is complete.
              </Alert>
            </Box>
          ) : (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                Last chance to change your mind!
              </Alert>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                This is your final confirmation. Click "Delete My Account" to proceed with permanent account deletion.
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Once you click the button below, we will immediately begin deleting all your data. This process cannot be stopped or reversed.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!deletingAccount && (
            <>
              <Button onClick={handleCloseConfirmDeleteDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                color="error"
                variant="contained"
                startIcon={<DeleteForever />}
              >
                Delete My Account
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
    </>
  );
};

export default ProfilePage;